/**
 * useRailDeletion.ts
 */
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import Rail from '@shared/classes/rail';
import { RailType } from '@shared/types/enums/rail';
import { keepWatchingSelector } from '@shared/store/slices/keepWatching.slice';
import { recordingSelector } from '@shared/store/slices/recordings.slice';
import { selectFocus } from '@shared/store/slices/focus.slice';
import { setLayoutIndex, setNavigation } from '@shared/store/slices/main.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef } from 'react';

type RailItem = Rail['data'][number];

interface Props {
  rails: Rail[];
  setRails: (rails: Rail[]) => void;
}

/**
 * `Category` (used by filter rails) doesn't carry an `eventId`, so a plain
 * `item?.eventId` access doesn't type-check against the full `Rail['data']`
 * union — narrow with an `in` check instead of casting.
 */
function getEventId(item: RailItem): string | undefined {
  return item && 'eventId' in item ? item.eventId : undefined;
}

/**
 * Reconciles the KeepWatching/Recording rails rendered by a page against the
 * `keepWatching`/`recordings` slices (already updated, item-by-item, by the
 * floating menu's Stop Watching/Delete Recording/Cancel Recording actions),
 * removing deleted items instantly instead of waiting for a full page
 * reload. Also moves focus to the next available item/rail, or the previous
 * one if none follows, so the layout never gets stuck on removed content.
 */
export default function useRailDeletion({ rails, setRails }: Props) {
  const dispatch = useAppDispatch();
  const focus = useAppSelector(selectFocus);
  const { data: keepWatchingData } = useAppSelector(keepWatchingSelector);
  const { pending, finished } = useAppSelector(recordingSelector);

  const railsRef = useRef(rails);
  railsRef.current = rails;

  useEffect(() => {
    // Avoid reconciling while a menu/confirmation overlay is focused.
    if (focus === Focus.MENU || focus === Focus.MODAL_BUTTONS) return;

    const validKeepWatchingIds = new Set(
      keepWatchingData.map((item) => item.id)
    );
    const validRecordingIds = new Set(
      [...pending, ...finished].map((item) => item.eventId)
    );

    let changed = false;
    const shrunk: { id: string; length: number }[] = [];
    const removedIds: string[] = [];

    const newRails = railsRef.current.reduce<Rail[]>((acc, rail) => {
      const isManaged =
        rail.type === RailType.KEEP_WATCHING ||
        rail.type === RailType.RECORDING;
      if (!isManaged) {
        acc.push(rail);
        return acc;
      }
      const validIds =
        rail.type === RailType.KEEP_WATCHING
          ? validKeepWatchingIds
          : validRecordingIds;
      const filteredData = rail.data.filter((item) => {
        const eventId = getEventId(item);
        return !!eventId && validIds.has(eventId);
      });
      if (filteredData.length === rail.data.length) {
        acc.push(rail);
        return acc;
      }
      changed = true;
      if (filteredData.length > 0) {
        acc.push(
          Object.assign(Object.create(Object.getPrototypeOf(rail)), rail, {
            data: filteredData,
          })
        );
        shrunk.push({ id: rail.id, length: filteredData.length });
      } else {
        removedIds.push(rail.id);
      }
      return acc;
    }, []);

    if (!changed) return;

    const { layout, selection } = store.getState().main;

    // Clamp the focused index of rails that shrank but still have items.
    shrunk.forEach(({ id, length }) => {
      const hIndex = layout[id]?.hIndex ?? 0;
      if (hIndex > length - 1) {
        dispatch(setLayoutIndex({ id, index: Math.max(0, length - 1) }));
      }
    });

    // Move focus off a rail that was fully removed, only if it was selected.
    removedIds.forEach((removedId) => {
      if (selection !== removedId) return;
      const nextId =
        layout[removedId]?.pointer?.next ||
        layout[removedId]?.pointer?.previous;
      if (!nextId) return; // No rails left; Empty template takes over.
      const nextRail = newRails.find((rail) => rail.id === nextId);
      const nextLength = nextRail?.data?.length ?? 0;
      const clampedIndex = Math.min(
        layout[nextId]?.hIndex ?? 0,
        Math.max(0, nextLength - 1)
      );
      dispatch(
        setNavigation({
          selection: nextId,
          asset: nextRail?.data?.[clampedIndex] as Event | undefined,
          layoutId: nextId,
          layoutIndex: clampedIndex,
        })
      );
    });

    setRails(newRails);
  }, [keepWatchingData, pending, finished, focus]);
}
